import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { SidebarModule } from "primeng/sidebar";
import { FormsModule } from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { ToastModule } from "primeng/toast";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import {
  auditTime,
  combineLatest,
  distinctUntilChanged,
  Subject,
  Subscription,
  take,
  takeUntil,
} from "rxjs";
import { filter } from "rxjs/operators";
import { ApiService } from "src/app/services/api/api.service";
import { ContextService } from "src/app/services/context/context.service";
import { ChatRoomInputComponent } from "./chat-room-input/chat-room-input.component";
import { registerLocaleData } from "@angular/common";
import localeFr from "@angular/common/locales/fr";
import { DomSanitizer } from "@angular/platform-browser";
import { SafeHtml } from "@angular/platform-browser";
import { ChatSocketService } from "src/app/services/chat-socket/chat-socket.service";
import { OverlayBadgeModule } from "primeng/overlaybadge";
import { BadgeModule } from "primeng/badge";
import { DialogModule } from "primeng/dialog";
import { MultiSelectModule } from "primeng/multiselect";
import { TooltipModule } from "primeng/tooltip";
import { Menu, MenuModule } from "primeng/menu";
import { PermissionsService } from "src/app/services/permissions/permissions.service";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { NavigationEnd, Router } from "@angular/router";
import { LayoutService } from "src/app/layout/service/layout.service";
registerLocaleData(localeFr);

@Component({
  selector: "app-chat-room",
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    SidebarModule,
    FormsModule,
    InputTextModule,
    ToastModule,
    ChatRoomInputComponent,
    OverlayBadgeModule,
    BadgeModule,
    DialogModule,
    MultiSelectModule,
    TooltipModule,
    MenuModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: "./chat-room.component.html",
  styleUrl: "./chat-room.component.scss",
})
export class ChatRoomComponent implements OnInit, OnDestroy {
  @ViewChild("messageContainer") private messageContainer!: ElementRef;
  showScrollToBottom = false;

  chatSidebarVisible: boolean = false;
  context: any;
  currentUser: any = null;

  // Chat room properties
  chatRooms: any[] = [];
  currentRoom: any = null;
  messages: any[] = [];
  isLoading: boolean = false;
  private isLoadingRooms: boolean = false;

  private destroy$ = new Subject<void>();
  hasMoreMessages: boolean = true;
  isLoadingMore: boolean = false;

  private roomCache: Record<
    number,
    { room: any; messages: any[]; hasMore: boolean }
  > = {};

  userMentioned: boolean = false;

  // Injections for work mode detection
  private router = inject(Router);
  private layoutService = inject(LayoutService);
  private currentUrl = signal(this.router.url);

  // Computed pour détecter le mode travail sur /chat
  isWorkModeOnChat = computed(() => {
    const workMode = this.layoutService.workMode();
    const currentUrl = this.currentUrl();
    return workMode && currentUrl.startsWith("/chat");
  });

  // Le widget de chat global est inutile (et chevauche le bouton d'envoi)
  // sur la page /chat elle-même : on masque le FAB dans ce cas.
  isChatRoute = computed(() => this.currentUrl().startsWith("/chat"));

  // HostBinding pour appliquer la classe CSS quand on est sur /chat en mode travail
  @HostBinding("class.work-mode-active") get workModeClass() {
    return this.isWorkModeOnChat();
  }

  // HostBinding pour le mode travail général (toutes pages)
  @HostBinding("class.work-mode-enabled") get workModeEnabledClass() {
    return this.layoutService.workMode();
  }

  constructor(
    private messageService: MessageService,
    private apiService: ApiService,
    private contextService: ContextService,
    private sanitizer: DomSanitizer,
    private chatSocketService: ChatSocketService,
    private permissionsService: PermissionsService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef,
  ) {}

  userRole: any;

  ngOnInit() {
    combineLatest([
      this.apiService.currentUserChange,
      this.contextService.context$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        auditTime(50),
        distinctUntilChanged((prev, curr) => {
          return (
            prev[0]?.id === curr[0]?.id &&
            prev[1]?.base?.id === curr[1]?.base?.id
          );
        }),
      )
      .subscribe(([user, context]) => {
        if (!user || !context) return;
        this.context = context;
        this.currentUser = user;
        this.userRole = this.permissionsService.getUserRole();
        this.loadChatRooms();
      });

    // 🔔 Abonnement global aux messages pour les notifications
    this.chatSocketService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        if (!msg || !this.currentUser) return;

        const isOwnMessage = msg.user_id === this.currentUser.id;
        const roomIsOpen =
          this.chatSidebarVisible &&
          this.currentRoom &&
          this.currentRoom.id === msg.roomId;

        // 🔔 Notification sonore (seulement pour les messages des autres)
        if (!isOwnMessage) {
          const userMentioned = msg.mentions_cache?.users?.some(
            (u: any) => u.id === this.currentUser?.id,
          );
          const audio = new Audio(
            userMentioned
              ? "/assets/notification-tag.mp3"
              : "/assets/notification.mp3",
          );
          audio.volume = userMentioned ? 0.4 : 0.2;
          audio.play().catch(() => {});
        }

        // 🧠 Traite et ajoute le message au cache
        const resolvedContent = this.resolveMessageTags(
          msg.content,
          msg.mentions_cache,
        );
        const highlightedContent = this.highlightMessage(
          msg.content,
          msg.mentions_cache,
        );
        const newMessage = {
          ...msg,
          content: resolvedContent,
          highlightedContent,
        };

        if (!this.roomCache[msg.roomId]) {
          this.roomCache[msg.roomId] = {
            room: this.chatRooms.find((r) => r.id === msg.roomId),
            messages: [newMessage],
            hasMore: true,
          };
        } else {
          this.roomCache[msg.roomId].messages.push(newMessage);
        }

        if ((roomIsOpen && !isOwnMessage) || msg.is_admin) {
          this.messages.push(newMessage);
          // Scroll automatiquement en bas après l'ajout
          this.markRoomAsRead();
          setTimeout(() => this.scrollToBottom(), 100);
        } else if (!isOwnMessage) {
          // Si le salon n'est pas ouvert, vérifie si l'utilisateur est mentionné
          this.messages.push(newMessage);
        }

        // 📈 Incrémente le compteur (seulement si ce n'est pas votre message ET que le salon n'est pas ouvert)
        const roomIndex = this.chatRooms.findIndex((r) => r.id === msg.roomId);
        if (roomIndex !== -1 && !isOwnMessage && !roomIsOpen) {
          this.chatRooms[roomIndex].unread_count =
            (this.chatRooms[roomIndex].unread_count || 0) + 1;
          this.cdr.detectChanges();
        }
      });

    // this.chatSocketService.read$.subscribe(event => {
    //   if (event.roomId === this.currentRoom?.id) {
    //     // Mettre à jour l'UI si nécessaire
    //     this.updateReadStatus(event.userId, event.lastReadMessageId);
    //   } else {
    //     // Mettre à jour le badge de notification pour les autres rooms
    //     const room = this.chatRooms.find(r => r.id === event.roomId);
    //     if (room && event.userId === this.currentUser.id) {
    //       room.unread_count = 0; // ou recalculer
    //     }
    //   }
    // });

    // Écouter les changements de route pour mettre à jour la classe work-mode-active
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.url);
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit() {
    if (this.messageContainer?.nativeElement) {
      this.messageContainer.nativeElement.addEventListener(
        "click",
        (event: MouseEvent) => {
          let target = event.target as HTMLElement;

          // Remonter jusqu’au span si l’enfant est cliqué
          while (
            target &&
            target !== this.messageContainer.nativeElement &&
            !target.classList.contains("doc-tag")
          ) {
            target = target.parentElement as HTMLElement;
          }

          if (target?.classList.contains("doc-tag")) {
            const docId = target.dataset["docId"]; // mieux que getAttribute
            const title = target.dataset["title"];
            if (docId) {
              window.open(`/files/${docId}`, "_blank");
            }
          }
        },
      );
    }

    if (this.messageContainer?.nativeElement) {
      this.messageContainer.nativeElement.addEventListener("scroll", () => {
        const el = this.messageContainer.nativeElement;

        // Si on est tout en haut et qu'il reste des messages à charger
        if (el.scrollTop === 0 && this.hasMoreMessages && !this.isLoadingMore) {
          this.loadOlderMessages();
        }
      });
    }

    const container = this.messageContainer.nativeElement;
    container.addEventListener("scroll", () => {
      const nearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        100;
      this.showScrollToBottom = !nearBottom;
    });
  }

  loadOlderMessages() {
    if (
      !this.currentRoom ||
      !this.context?.base?.id ||
      this.messages.length === 0
    )
      return;

    const oldestMessage = this.messages[0];
    const delimId = oldestMessage.id;
    this.isLoadingMore = true;

    const el = this.messageContainer.nativeElement;
    const previousScrollHeight = el.scrollHeight;

    this.apiService
      .getRoom(this.context.base.id, this.currentRoom.id, delimId)
      .subscribe({
        next: (data: any) => {
          const olderMessages = data.messages.map((msg: any) => ({
            ...msg,
            content: this.resolveMessageTags(msg.content, msg.mentions_cache),
            highlightedContent: this.highlightMessage(
              msg.content,
              msg.mentions_cache,
            ),
          }));

          if (olderMessages.length === 0) {
            this.hasMoreMessages = false;
          } else {
            // ⚡ Ajoute les anciens messages avant ceux existants
            this.messages = [...olderMessages, ...this.messages];

            // 🧩 Repositionne le scroll à la bonne hauteur (pas en bas)
            setTimeout(() => {
              const newScrollHeight = el.scrollHeight;
              el.scrollTop = newScrollHeight - previousScrollHeight;
            }, 50);
          }

          this.isLoadingMore = false;
        },
        error: (err) => {
          console.error("Erreur lors du chargement des anciens messages", err);
          this.isLoadingMore = false;
        },
      });
  }

  scrollToBottom() {
    const container = this.messageContainer.nativeElement;
    container.scrollTo({
      top: container.scrollHeight,
      behavior: "smooth",
    });
  }

  ngOnDestroy() {
    this.messagesSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  resetChatState(): void {
    this.currentRoom = null;
    this.messages = [];
    this.chatRooms = [];
  }

  previousBaseId: number | null = null;

  // Add ngOnChanges to detect context changes
  ngOnChanges(changes: SimpleChanges): void {
    // Check if company has changed
    if (changes["context"] && this.context?.base?.id !== this.previousBaseId) {
      this.resetChatState();
      this.previousBaseId = this.context?.base?.id;

      // If sidebar is open, reload chat rooms for the new company
      if (this.chatSidebarVisible && this.context?.base?.id) {
        this.loadChatRooms();
      }
    }
  }

  // Modify toggleSidebar to reset state when opening
  toggleSidebar(): void {
    this.chatSidebarVisible = !this.chatSidebarVisible;

    // When opening sidebar, check if we need to load rooms
    if (this.chatSidebarVisible) {
      this.userMentioned = false;
      // Only reload if we have a valid company context
      if (this.context?.base?.id) {
        // If current context doesn't match previous context, reset state
        if (this.context.base.id !== this.previousBaseId) {
          this.resetChatState();
          this.previousBaseId = this.context.base.id;
        }

        this.loadChatRooms(true);
      } else {
        this.resetChatState();
      }
    }
  }

  loadChatRooms(preloadChat: boolean = false) {
    if (!this.context?.base?.id) return;

    // Guard: Prevent multiple simultaneous calls
    if (this.isLoadingRooms) return;

    this.isLoading = true;
    this.isLoadingRooms = true;
    this.apiService.getCompanyChatRooms(this.context.base.id).subscribe({
      next: (rooms) => {
        this.chatRooms = rooms;
        // trier les salons en mettant en premier celui qui a company_id_if_global 1
        this.chatRooms.sort(
          (a, b) =>
            (b.company_id_if_global || 0) - (a.company_id_if_global || 0),
        );
        this.isLoading = false;
        this.isLoadingRooms = false;

        // 🔥 Se connecter à chaque salon pour recevoir les messages même si le chat n'est pas ouvert
        this.chatRooms.forEach((room) => {
          this.chatSocketService.connect(room.id);
        });

        if (rooms.length > 0 && !this.currentRoom && preloadChat) {
          this.selectRoom(rooms[0]);
          // setTimeout(() => this.scrollToBottom(), 100);
        } else if (rooms.length > 0 && this.currentRoom) {
          // Si un salon est déjà sélectionné, le re-sélectionner pour rafraîchir les messages
          this.markRoomAsRead();
        }
        setTimeout(() => this.scrollToBottom(), 100);
      },
      error: (error) => {
        console.error("Error loading chat rooms", error);
        this.isLoading = false;
        this.isLoadingRooms = false;
        this.messageService.add({
          severity: "error",
          summary: "Erreur",
          detail: "Impossible de charger les salons de discussion",
        });
      },
    });
  }

  private messagesSub: Subscription | null = null;

  selectRoom(room: any) {
    if (!this.context?.base?.id) return;

    this.messagesSub?.unsubscribe();

    this.markRoomAsRead();

    this.chatSocketService.connect(room.id);
    this.replyToMessage = null;

    this.messagesSub = this.chatSocketService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        if (msg.room_id !== room.id || msg.user_id === this.currentUser?.id)
          return;

        const resolvedContent = this.resolveMessageTags(
          msg.content,
          msg.mentions_cache,
        );
        const highlightedContent = this.highlightMessage(
          msg.content,
          msg.mentions_cache,
        );
        const newMessage = {
          ...msg,
          content: resolvedContent,
          highlightedContent,
        };

        // ⚡ If the current room is active
        if (this.currentRoom?.id === room.id) {
          // Add the message to the UI
          this.messages.push(newMessage);
          setTimeout(() => this.scrollToBottom(), 100);

          // Mark the room as read since the user is currently viewing it
          this.apiService
            .markRoomAsRead(this.context.base.id, room.id)
            .subscribe({
              error: (error) =>
                console.error(
                  "Error marking room as read after new message",
                  error,
                ),
            });
        }

        // 🔁 Update the cache
        if (this.roomCache[room.id]) {
          this.roomCache[room.id].messages.push(newMessage);
        }
      });

    // ⚡ Vérifie si le salon est déjà en cache
    if (this.roomCache[room.id]) {
      this.currentRoom = this.roomCache[room.id].room;
      this.messages = [...this.roomCache[room.id].messages];
      this.messages = this.messages
        .filter((msg) => msg.id) // Garde seulement ceux avec un id
        .filter(
          (msg, index, self) =>
            index === self.findIndex((m) => m.id === msg.id), // Supprime les doublons
        );
      this.hasMoreMessages = this.roomCache[room.id].hasMore;
      this.markRoomAsRead();

      setTimeout(() => this.scrollToBottom(), 100);
      return;
    }

    // Sinon, charge depuis l’API
    this.isLoading = true;
    this.apiService.getRoom(this.context.base.id, room.id, 0).subscribe({
      next: (data) => {
        const messages = data.messages.map((msg: any) => ({
          ...msg,
          content: this.resolveMessageTags(msg.content, msg.mentions_cache),
          highlightedContent: this.highlightMessage(
            msg.content,
            msg.mentions_cache,
          ),
        }));

        this.currentRoom = data.room;
        this.currentRoom.users =
          this.chatRooms.find((r) => r.id === room.id)?.users || [];
        this.messages = messages;
        this.hasMoreMessages = true;
        this.isLoading = false;
        this.markRoomAsRead();

        // for the room just loaded, set the unread count to 0
        const roomIndex = this.chatRooms.findIndex((r) => r.id === room.id);
        if (roomIndex !== -1) {
          this.chatRooms[roomIndex].unread_count = 0;
        }

        // 🧠 Mets en cache
        this.roomCache[room.id] = {
          room: data.room,
          messages: [...messages],
          hasMore: true,
        };

        setTimeout(() => this.scrollToBottom(), 100);
      },
      error: (error) => {
        console.error("Error loading chat room", error);
        this.isLoading = false;
        this.messageService.add({
          severity: "error",
          summary: "Erreur",
          detail: "Impossible de charger les messages",
        });
      },
    });
  }

  isSendingMessage: boolean = false;

  @ViewChild("chatInput") chatInput!: any;

  // Then modify your onMessageSent method
  onMessageSent(event: any) {
    if (!this.context?.base?.id || !this.currentRoom?.id) {
      this.messageService.add({
        severity: "error",
        summary: "Erreur",
        detail: "Impossible d'envoyer le message: aucun salon sélectionné",
      });
      return;
    }

    const messageContent = event.message;
    if (!messageContent.trim()) return;

    this.isSendingMessage = true;
    this.apiService
      .sendMessageToRoom(this.context.base.id, this.currentRoom.id, {
        content: messageContent,
        is_priority: event.isPriority || false,
        is_check: true,
        mentions_cache: { users: [], documents: [] },
        reply_to_id: this.replyToMessage?.id || null,
      })
      .subscribe({
        next: (response) => {
          const resolvedContent = this.resolveMessageTags(
            response.content,
            response.mentions_cache,
          );
          const highlightedContent = this.highlightMessage(
            response.content,
            response.mentions_cache,
          );

          // Créer l'objet message complet
          const newMessage = {
            ...response,
            user: this.currentUser,
            content: resolvedContent,
            highlightedContent,
            reply_to: this.replyToMessage || response.reply_to,
          };

          // Ajouter le message à l'affichage actuel
          this.messages.push(newMessage);

          // IMPORTANT: Mettre à jour le cache de la room pour que le message soit
          // visible quand on revient dans cette room
          if (this.roomCache[this.currentRoom.id]) {
            this.roomCache[this.currentRoom.id].messages.push(newMessage);
          }

          this.replyToMessage = null;
          this.isSendingMessage = false;
          setTimeout(() => this.scrollToBottom(), 100);
          setTimeout(() => this.chatInput?.focusInput(), 50);
        },
        error: (error) => {
          console.error("Error sending message", error);
          this.isSendingMessage = false;
          this.messageService.add({
            severity: "error",
            summary: "Erreur",
            detail: "Impossible d'envoyer le message",
          });
        },
      });
  }

  isCurrentUserMessage(message: any): boolean {
    // Replace 'this.currentUser.id' with however you store the current user's ID
    return message.user_id === this.currentUser?.id;
  }

  isFirstMessageOfDay(message: any, index: number): boolean {
    if (index === 0) return true;

    const currentDate = new Date(message.created_at).toDateString();
    const previousDate = new Date(
      this.messages[index - 1].created_at,
    ).toDateString();

    return currentDate !== previousDate;
  }

  refreshCurrentRoom() {
    if (!this.context?.base?.id || !this.currentRoom?.id) {
      return;
    }

    this.isLoading = true;
    this.apiService
      .getRoom(this.context.base.id, this.currentRoom.id, 0)
      .subscribe({
        next: (data) => {
          this.currentRoom = data.room;

          // Résoudre les tags et générer le HTML coloré pour chaque message
          this.messages = data.messages.map((msg: any) => {
            const resolvedContent = this.resolveMessageTags(
              msg.content,
              msg.mentions_cache,
            );
            const highlightedContent = this.highlightMessage(
              msg.content,
              msg.mentions_cache,
            );

            return {
              ...msg,
              content: resolvedContent,
              highlightedContent,
            };
          });

          this.messages.sort(
            (a, b) =>
              new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime(),
          );

          this.isLoading = false;
          setTimeout(() => this.scrollToBottom(), 100); // Petit délai pour s'assurer que le DOM est mis à jour
        },
        error: (error) => {
          console.error("Error refreshing chat room", error);
          this.isLoading = false;
          this.messageService.add({
            severity: "error",
            summary: "Erreur",
            detail: "Impossible d'actualiser les messages",
          });
        },
      });
  }

  private loadedUsers = new Map<number, string>(); // id => displayName
  private loadedDocs = new Map<number, string>(); // id => title

  private resolveMessageTags(message: string, mentions_cache: any): string {
    if (!message) return "";

    let resolved = message;

    // User tags: @u:id OR @[Name](user:id)
    (mentions_cache?.users || []).forEach((u: any) => {
      // Match @u:id
      const regex1 = new RegExp(`@u:${u.id}`, "g");
      resolved = resolved.replace(regex1, `@${u.name}`);

      // Match @[Name](user:id)
      const regex2 = new RegExp(`@\\[.*?\\]\\(user:${u.id}\\)`, "g");
      resolved = resolved.replace(regex2, `@${u.name}`);
    });

    // Document tags: #d:id OR #[Title](doc:id)
    (mentions_cache?.documents || []).forEach((d: any) => {
      const regex1 = new RegExp(`#d:${d.id}`, "g");
      resolved = resolved.replace(regex1, `#${d.title}`);

      const regex2 = new RegExp(`#\\[.*?\\]\\(doc:${d.id}\\)`, "g");
      resolved = resolved.replace(regex2, `#${d.title}`);
    });

    return resolved;
  }

  private highlightMessage(content: string, mentions_cache: any): SafeHtml {
    if (!content) return "";

    // 🔒 Fonction pour échapper le HTML
    const escapeHtml = (str: string) =>
      str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    // Résout les tags (user/doc) déjà transformés en texte brut
    let highlighted = escapeHtml(
      this.resolveMessageTags(content, mentions_cache),
    );

    // 🔵 Surbrillance spéciale pour @everyone
    highlighted = highlighted.replace(
      /(^|\s)(@everyone)\b/g,
      `$1<span class="text-purple-600 bg-purple-100 rounded-sm font-semibold cursor-pointer">@everyone</span>`,
    );

    // 🟢 Tags documents
    (mentions_cache?.documents || []).forEach((d: any) => {
      const safeTitle = d.title.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");

      highlighted = highlighted.replace(
        new RegExp(`#${safeTitle}`, "g"),
        `<span class="doc-tag text-green-600 bg-green-100 rounded-sm cursor-pointer hover:bg-green-200 transition" data-doc-id="${d.id}" data-title="${escapeHtml(d.title)}">#${escapeHtml(d.title)}</span>`,
      );
    });

    // 🔵 Tags utilisateurs
    (mentions_cache?.users || []).forEach((u: any) => {
      const safeName = u.name.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
      highlighted = highlighted.replace(
        new RegExp(`@${safeName}`, "g"),
        `<span class="text-blue-500 bg-blue-100 rounded-sm cursor-pointer hover:bg-blue-200 transition">@${escapeHtml(u.name)}</span>`,
      );
    });

    const linkified = this.linkifyContent(highlighted);
    const withLineBreaks = linkified.replace(/\n/g, "<br>");
    // 🛡️ Bypass security pour conserver les data-attributes
    return this.sanitizer.bypassSecurityTrustHtml(withLineBreaks);
  }

  getUserColor(userId: number): string {
    const hue = (userId * 137) % 360; // 137 = "nombre premier" pour disperser les couleurs
    return `hsl(${hue}, 60%, 90%)`; // couleur pastel claire
  }

  createRoomModalVisible: boolean = false;
  companyUsers: any[] = [];
  newRoom: any = {
    name: "",
    user_ids: [],
  };
  isCreatingRoom: boolean = false;

  openCreateRoomModal() {
    this.newRoom = {
      name: "",
      user_ids: [],
    };
    this.loadCompanyUsers();
    this.createRoomModalVisible = true;
  }

  // Charger les utilisateurs de la compagnie
  loadCompanyUsers() {
    // Récupérer l'ID de la compagnie active depuis le contexte
    const companyId = this.context?.base?.id;
    if (!companyId) {
      console.error("No company ID found in context");
      return;
    }

    this.apiService.getCompanyUsers(companyId).subscribe({
      next: (response) => {
        this.companyUsers = response.map((user: any) => ({
          id: user.id,
          display_name: `${user.firstname} ${user.name}`,
        }));
      },
      error: (error) => {
        console.error("Error loading company users:", error);
        this.messageService.add({
          severity: "error",
          summary: "Erreur",
          detail: "Impossible de charger les utilisateurs",
          life: 3000,
        });
      },
    });
  }

  // Créer une nouvelle room
  createRoom() {
    if (!this.newRoom.name || this.newRoom.user_ids.length === 0) {
      return;
    }

    this.isCreatingRoom = true;
    const companyId = this.context?.base?.id;

    // if this.currentUser.id is not in newRoom.user_ids, add it
    if (!this.newRoom.user_ids.includes(this.currentUser.id)) {
      this.newRoom.user_ids.push(this.currentUser.id);
    }

    this.apiService
      .createRoom(companyId, this.newRoom.name, this.newRoom.user_ids)
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: "success",
            summary: "Succès",
            detail: "Salon créé avec succès",
            life: 3000,
          });
          this.createRoomModalVisible = false;

          // Rafraîchir la liste des rooms
          this.loadChatRooms();
        },
        error: (error) => {
          console.error("Error creating room:", error);
          this.messageService.add({
            severity: "error",
            summary: "Erreur",
            detail: "Impossible de créer le salon",
            life: 3000,
          });
        },
        complete: () => {
          this.isCreatingRoom = false;
        },
      });
  }

  roomUsers: any[] = [];
  roomUsersModalVisible: boolean = false;

  // Fonction pour ouvrir la modale des membres de la room
  openRoomUsers() {
    if (!this.currentRoom || !this.currentRoom.users) return;

    this.roomUsers = this.currentRoom.users;
    this.roomUsersModalVisible = true;
  }

  // Fonction utilitaire pour obtenir les initiales d'un utilisateur
  getInitials(user: any): string {
    return `${user.firstname?.charAt(0) || ""}${user.name?.charAt(0) || ""}`;
  }

  replyToMessage: any = null;

  setReplyTo(message: any) {
    this.replyToMessage = message;
    setTimeout(() => this.chatInput?.focusInput(), 50);
  }

  cancelReply() {
    this.replyToMessage = null;
  }

  @ViewChild("messageMenu") messageMenu!: Menu;
  menuItems: MenuItem[] = [];
  selectedMessage: any = null;
  openMessageMenu(event: MouseEvent, message: any) {
    const target = event.target as HTMLElement;

    // Si on clique sur un doc-tag, laisse le click s'exécuter normalement
    if (target.closest(".doc-tag")) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.selectedMessage = message;

    this.menuItems = [
      {
        label: "Répondre",
        icon: "pi pi-reply",
        command: () => this.setReplyTo(this.selectedMessage),
      },
    ];

    this.messageMenu.toggle(event);
  }

  scrollToMessage(messageId: any) {
    setTimeout(() => {
      const element = document.getElementById("message-" + messageId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });

        // Add highlight class
        element.classList.add("highlighted-message");

        // Remove highlight after animation completes
        setTimeout(() => {
          element.classList.remove("highlighted-message");
        }, 2000);
      }
    }, 100);
  }

  //edit d'une room

  editRoomModalVisible: boolean = false;
  isUpdatingRoom: boolean = false;

  editRoom: any = {
    id: null,
    name: "",
    user_ids: [],
  };

  // 🔧 Ouvre la modale d'édition avec les infos actuelles du salon
  openEditRoomModal() {
    if (!this.currentRoom) return;

    // Charger les utilisateurs si pas encore fait
    if (this.companyUsers.length === 0) {
      this.loadCompanyUsers();
    }

    this.editRoom = {
      id: this.currentRoom.id,
      name: this.currentRoom.name,
      user_ids: this.currentRoom.users?.map((u: any) => u.id) || [],
    };

    this.editRoomModalVisible = true;
  }

  // 💾 Appel API pour mettre à jour le salon
  updateRoom() {
    if (!this.context?.base?.id || !this.editRoom.id) return;

    this.isUpdatingRoom = true;

    this.apiService
      .updateRoom(
        this.context.base.id,
        this.editRoom.id,
        this.editRoom.name,
        this.editRoom.user_ids,
      )
      .subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: "success",
            summary: "Succès",
            detail: "Salon mis à jour avec succès",
            life: 3000,
          });

          // Met à jour le nom et la liste d’utilisateurs localement
          if (this.currentRoom && this.currentRoom.id === this.editRoom.id) {
            this.currentRoom.name = this.editRoom.name;
            this.currentRoom.users = this.companyUsers.filter((u) =>
              this.editRoom.user_ids.includes(u.id),
            );
          }

          // Met à jour la liste des rooms dans la sidebar
          const index = this.chatRooms.findIndex(
            (r) => r.id === this.editRoom.id,
          );
          if (index !== -1) {
            this.chatRooms[index].name = this.editRoom.name;
            this.chatRooms[index].users = this.currentRoom.users;
          }

          this.isUpdatingRoom = false;
          this.editRoomModalVisible = false;
        },
        error: (error) => {
          console.error("Erreur updateRoom", error);
          this.isUpdatingRoom = false;
          this.messageService.add({
            severity: "error",
            summary: "Erreur",
            detail: "Impossible de modifier le salon",
            life: 3000,
          });
        },
      });
  }

  // delete room

  confirmDeleteRoom() {
    if (!this.currentRoom) return;

    this.confirmationService.confirm({
      message: `Voulez-vous vraiment supprimer le salon <b>${this.currentRoom.name}</b> ?`,
      header: "Confirmer la suppression",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Oui, supprimer",
      rejectLabel: "Annuler",
      acceptButtonStyleClass: "p-button-danger",
      rejectButtonStyleClass: "p-button-text",
      accept: () => this.deleteRoom(),
    });
  }

  deleteRoom() {
    if (!this.context?.base?.id || !this.currentRoom) return;

    this.isUpdatingRoom = true;

    this.apiService
      .deleteRoom(this.context.base.id, this.currentRoom.id)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Salon supprimé",
            detail: `"${this.currentRoom.name}" a été supprimé avec succès.`,
            life: 3000,
          });

          // Retire le salon de la liste locale
          this.chatRooms = this.chatRooms.filter(
            (r) => r.id !== this.currentRoom.id,
          );

          // Ferme la modale et réinitialise
          this.editRoomModalVisible = false;
          this.currentRoom = null;

          this.isUpdatingRoom = false;
        },
        error: (err) => {
          console.error("Erreur deleteRoom", err);
          this.messageService.add({
            severity: "error",
            summary: "Erreur",
            detail: "Impossible de supprimer le salon.",
            life: 3000,
          });
          this.isUpdatingRoom = false;
        },
      });
  }

  markRoomAsRead() {
    if (!this.currentRoom) return;

    this.apiService
      .markRoomAsRead(this.context.base.id, this.currentRoom.id)
      .subscribe({
        next: () => {
          const roomIndex = this.chatRooms.findIndex(
            (r) => r.id === this.currentRoom.id,
          );
          if (roomIndex !== -1) {
            this.chatRooms[roomIndex].unread_count = 0;
          }
        },
        error: (err) => {
          console.error("Erreur markRoomAsRead", err);
        },
      });
  }

  // markRoomAsRead() {
  //   if (!this.currentRoom || this.messages.length === 0) return;

  //   const lastMessage = this.messages[this.messages.length - 1];

  //   // Appel API pour persister + broadcast automatique
  //   this.chatSocketService.markAsRead(this.currentRoom.id, lastMessage.id)
  //     .subscribe({
  //       next: () => {
  //         this.currentRoom!.unread_count = 0;
  //       },
  //       error: (err) => console.error('Error marking as read:', err)
  //     });
  // }

  async scrollToOrLoadMessage(targetMessageId: number) {
    // Vérifie si le message est déjà dans les messages affichés
    const existing = this.messages.find((m) => m.id === targetMessageId);
    if (existing) {
      this.scrollToMessage(targetMessageId);
      return;
    }

    // Tant qu’on a encore des messages à charger
    while (this.hasMoreMessages && !this.isLoadingMore) {
      await new Promise<void>((resolve) => {
        this.isLoadingMore = true;

        const oldestMessage = this.messages[0];
        const delimId = oldestMessage.id;

        this.apiService
          .getRoom(this.context.base.id, this.currentRoom.id, delimId)
          .pipe(take(1))
          .subscribe({
            next: (data: any) => {
              const olderMessages = data.messages.map((msg: any) => ({
                ...msg,
                content: this.resolveMessageTags(
                  msg.content,
                  msg.mentions_cache,
                ),
                highlightedContent: this.highlightMessage(
                  msg.content,
                  msg.mentions_cache,
                ),
              }));

              if (olderMessages.length === 0) {
                this.hasMoreMessages = false;
              } else {
                this.messages = [...olderMessages, ...this.messages];
              }

              this.isLoadingMore = false;
              resolve();
            },
            error: (err) => {
              console.error(
                "Erreur lors du chargement des anciens messages",
                err,
              );
              this.isLoadingMore = false;
              this.hasMoreMessages = false;
              resolve();
            },
          });
      });

      // Vérifie après chargement si le message cible est maintenant là
      const found = this.messages.find((m) => m.id === targetMessageId);
      if (found) {
        setTimeout(() => this.scrollToMessage(targetMessageId), 100);
        return;
      }
    }

    // Si on sort de la boucle sans avoir trouvé le message
    console.warn(
      `Message #${targetMessageId} introuvable même après chargement complet.`,
    );
  }

  hasUnreadMessages(): boolean {
    // More robust check that won't return false negatives
    if (!this.chatRooms || !Array.isArray(this.chatRooms)) {
      return false;
    }

    // Debug logging to see real values when the check happens
    const hasUnread = this.chatRooms.some(
      (room) => (room.unread_count || 0) > 0,
    );

    return hasUnread;
  }

  private linkifyContent(text: string): string {
    if (!text) return "";

    // Regex pour détecter les URLs
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;

    // Remplacer les URLs par des liens HTML
    return text.replace(urlRegex, (url) => {
      let href = url;

      // Ajouter https:// si l'URL commence par www.
      if (url.startsWith("www.")) {
        href = "https://" + url;
      }

      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="font-bold text-blue-600 hover:text-blue-800 underline" onclick="event.stopPropagation()">${url}</a>`;
    });
  }
}
